import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Shield, FileDown, Upload, FileText, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { FreezeDryerSupport } from '../components/FreezeDryerSupport';
import { WarrantyClaimForm } from '../components/WarrantyClaimForm';
import { WarrantyPolicy } from '../components/WarrantyPolicy';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Warranty = () => {
  const [customContent, setCustomContent] = useState(null);
  const [warrantyPdf, setWarrantyPdf] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/settings`);
        if (res.data.warranty_content) {
          setCustomContent(res.data.warranty_content);
        }
      } catch (e) {
        console.error('Failed to fetch settings:', e);
      }
    };
    
    const fetchWarrantyPdf = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/warranty/pdf`);
        if (res.data.has_pdf) {
          setWarrantyPdf(res.data);
        }
      } catch (e) {
        console.error('Failed to fetch warranty PDF:', e);
      }
    };
    
    const checkAdminStatus = () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const payload = JSON.parse(atob(token.split('.')[1]));
          if (payload.role === 'admin' || payload.role === 'superadmin') {
            setIsAdmin(true);
          }
        }
      } catch (e) {
        // Not logged in or invalid token
      }
    };
    
    fetchSettings();
    fetchWarrantyPdf();
    checkAdminStatus();
  }, []);

  const handlePdfUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please select a PDF file');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }
    
    const formData = new FormData();
    formData.append('file', file);
    
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_URL}/api/warranty/upload-pdf`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
      
      setWarrantyPdf({
        has_pdf: true,
        file_url: res.data.file_url,
        filename: res.data.filename
      });
      toast.success('Warranty PDF uploaded successfully');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to upload PDF');
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePdf = async () => {
    if (!confirm('Are you sure you want to delete the warranty PDF?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/warranty/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setWarrantyPdf(null);
      toast.success('Warranty PDF deleted');
    } catch (e) {
      toast.error('Failed to delete PDF');
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      
      {/* Hero Section */}
      <div className="pt-44 pb-12 bg-gradient-to-br from-[#003456] to-[#002B47]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="w-20 h-20 bg-[#CE8946] rounded-full flex items-center justify-center mx-auto mb-6">
            <Shield className="h-10 w-10 text-[#003456]" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Warranty Information</h1>
          <p className="text-xl text-white/80 max-w-2xl mx-auto mb-6">
            Every LyphLine rebuild is backed by our 90 day rebuild warranty. Machine registration and warranty
            claims are handled through <strong>Freeze Dryer Support</strong>.
          </p>
          
          {/* Download Warranty PDF Button */}
          {warrantyPdf && warrantyPdf.has_pdf && (
            <a 
              href={`${API_URL}${warrantyPdf.file_url}`} 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#CE8946] hover:bg-[#B87333] text-[#003456] font-semibold px-6 py-3 rounded-lg transition-colors"
              data-testid="download-warranty-pdf"
            >
              <FileDown className="h-5 w-5" />
              Download Full Warranty (PDF)
            </a>
          )}
        </div>
      </div>
      
      {/* Admin PDF Upload Section */}
      {isAdmin && (
        <div className="max-w-4xl mx-auto px-6 mt-8">
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                <FileText className="h-5 w-5 text-amber-700" />
              </div>
              <div>
                <h3 className="font-semibold text-amber-900">Admin: Warranty PDF Management</h3>
                <p className="text-sm text-amber-700">Upload or replace the warranty PDF document</p>
              </div>
            </div>
            
            {warrantyPdf && warrantyPdf.has_pdf ? (
              <div className="flex items-center justify-between bg-white rounded-lg p-4 border border-amber-200">
                <div className="flex items-center gap-3">
                  <FileText className="h-8 w-8 text-red-500" />
                  <div>
                    <p className="font-medium text-gray-900">{warrantyPdf.filename || 'warranty.pdf'}</p>
                    <a 
                      href={`${API_URL}${warrantyPdf.file_url}`} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-sm text-blue-600 hover:underline"
                    >
                      View PDF
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <label className="cursor-pointer">
                    <input 
                      type="file" 
                      accept=".pdf" 
                      onChange={handlePdfUpload}
                      className="hidden" 
                      data-testid="warranty-pdf-replace-input"
                    />
                    <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium">
                      <Upload className="h-4 w-4" />
                      Replace
                    </span>
                  </label>
                  <Button 
                    onClick={handleDeletePdf}
                    variant="outline"
                    className="border-red-300 text-red-600 hover:bg-red-50"
                    data-testid="warranty-pdf-delete-btn"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ) : (
              <label className="cursor-pointer block">
                <input 
                  type="file" 
                  accept=".pdf" 
                  onChange={handlePdfUpload}
                  className="hidden" 
                  data-testid="warranty-pdf-upload-input"
                />
                <div className="border-2 border-dashed border-amber-300 rounded-lg p-8 text-center hover:border-amber-500 transition-colors bg-white">
                  {uploading ? (
                    <div className="flex flex-col items-center gap-2">
                      <div className="animate-spin rounded-full h-8 w-8 border-2 border-amber-500 border-t-transparent"></div>
                      <p className="text-amber-700">Uploading...</p>
                    </div>
                  ) : (
                    <>
                      <Upload className="h-10 w-10 text-amber-500 mx-auto mb-3" />
                      <p className="font-medium text-amber-900">Click to upload warranty PDF</p>
                      <p className="text-sm text-amber-600 mt-1">PDF files up to 10MB</p>
                    </>
                  )}
                </div>
              </label>
            )}
          </div>
        </div>
      )}
      
      <div className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          {customContent ? (
            <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12 prose prose-lg max-w-none">
              <div className="text-gray-600" dangerouslySetInnerHTML={{ __html: customContent }} />
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <WarrantyPolicy testid="warranty-page-policy" />
            </div>
          )}
        </div>
      </div>

      {/* Freeze Dryer Support + on-site claim capture */}
      <FreezeDryerSupport bg="bg-[#003456]" testid="warranty-page-support" />
      <WarrantyClaimForm bg="bg-[#F8FAFC]" testid="warranty-page-claim-form" />

      <Footer />
    </div>
  );
};

export default Warranty;
